import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { isOrgAdmin } from '../components/Layout';
import { Button, Card, CopyField, cx, Dialog, Empty, ErrorNote, Field, inputClass, Loading, PageHeader, Section } from '../components/ui';
import { api, unwrap, type Member, type Members as MembersData } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useManagedOrgs, useMembers } from '../lib/queries';

type Role = 'admin' | 'viewer';
const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function Members() {
  const { me, vardexAdmin, orgs } = useManagedOrgs();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org') ?? orgs[0]?.id;
  const org = orgs.find((o) => o.id === orgId);
  const members = useMembers(orgId);

  if (me.isPending) return <Loading />;
  if (me.data && !isOrgAdmin(me.data) && !vardexAdmin) return <Empty>Only organization admins manage members.</Empty>;

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={org?.name}
        actions={
          orgs.length > 1 && (
            <select className={cx(inputClass, 'w-auto')} value={orgId} onChange={(e) => setParams({ org: e.target.value })} aria-label="Organization">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )
        }
      />
      {!orgId ? <Empty>No organization.</Empty> : members.isPending ? <Loading /> : members.error ? <ErrorNote error={members.error} /> : (
        <MembersView org={orgId} data={members.data} selfId={me.data?.user.id} />
      )}
    </>
  );
}

function MembersView({ org, data, selfId }: { org: string; data: MembersData; selfId?: string }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['members', org] });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const plantName = new Map(data.plants.map((p) => [p.id, p.name]));
  const plantsText = (role: Role, ids: number[]) => (role === 'admin' ? 'All plants' : ids.length ? ids.map((id) => plantName.get(id) ?? id).join(', ') : 'No plants');

  const remove = useMutation({
    mutationFn: (m: Member) => unwrap(api.DELETE('/organizations/{org}/members/{userId}', { params: { path: { org, userId: m.userId } } })),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/invitations/{id}', { params: { path: { id } } })),
    onSuccess: refresh,
  });

  return (
    <>
      <Section title="People" actions={<Button variant="primary" onClick={() => setInviting(true)}>Invite</Button>}>
        <ErrorNote error={remove.error ?? cancel.error} />
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Access</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.userId} className="border-t border-rule">
                  <td className="px-3 py-2">
                    <div className="font-medium">{m.name}{m.userId === selfId && <span className="ml-1 text-xs text-muted">(you)</span>}</div>
                    <div className="text-xs text-muted">{m.email}</div>
                  </td>
                  <td className="px-3 py-2 capitalize">{m.role}</td>
                  <td className="px-3 py-2 text-muted">{plantsText(m.role, m.plantIds)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button variant="ghost" onClick={() => setEditing(m)}>Edit</Button>
                    <Button variant="ghost" className="text-critical" onClick={() => { if (confirm(`Remove ${m.name} from the organization?`)) remove.mutate(m); }}>Remove</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Section>

      <Section title="Open invitations">
        {data.invitations.length === 0 ? <Empty>No open invitations.</Empty> : (
          <div className="space-y-2">
            {data.invitations.map((i) => (
              <Card key={i.id} className="p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="font-medium">{i.email}</span>
                    <span className="text-muted"> · <span className="capitalize">{i.role}</span> · {plantsText(i.role, i.plantIds)} · until {fmtDate(i.expiresAt, localTz)}</span>
                  </div>
                  <Button variant="ghost" className="text-critical" onClick={() => cancel.mutate(i.id)}>Cancel</Button>
                </div>
                <CopyField value={i.url} label="Invitation link" />
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Dialog open={inviting} onClose={() => { setInviting(false); refresh(); }} title="Invite someone">
        <InviteForm org={org} plants={data.plants} onDone={refresh} />
      </Dialog>
      <Dialog open={!!editing} onClose={() => setEditing(null)} title={`Access for ${editing?.name ?? ''}`}>
        {editing && <AccessForm org={org} member={editing} plants={data.plants} onDone={() => { setEditing(null); refresh(); }} />}
      </Dialog>
    </>
  );
}

function RolePlants({ role, setRole, plantIds, setPlantIds, plants }: {
  role: Role; setRole: (r: Role) => void; plantIds: number[]; setPlantIds: (ids: number[]) => void; plants: MembersData['plants'];
}) {
  return (
    <>
      <Field label="Role">
        <div className="space-y-1 text-sm">
          {([['viewer', 'Viewer', 'Sees the plants you pick below'], ['admin', 'Admin', 'All plants, plus members and devices']] as const).map(([value, label, hint]) => (
            <label key={value} className="flex items-start gap-2">
              <input type="radio" name="role" checked={role === value} onChange={() => setRole(value)} className="mt-1 accent-[var(--accent)]" />
              <span><span className="font-medium">{label}</span> <span className="text-muted">{hint}</span></span>
            </label>
          ))}
        </div>
      </Field>
      {role === 'viewer' && (
        <Field label="Plants">
          {plants.length === 0 ? <p className="text-sm text-muted">This organization has no plants yet.</p> : (
            <div className="space-y-1 text-sm">
              {plants.map((p) => (
                <label key={p.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={plantIds.includes(p.id)}
                    onChange={(e) => setPlantIds(e.target.checked ? [...plantIds, p.id] : plantIds.filter((x) => x !== p.id))}
                    className="accent-[var(--accent)]"
                  />
                  {p.name} <span className="text-muted">{p.slug}</span>
                </label>
              ))}
            </div>
          )}
        </Field>
      )}
    </>
  );
}

export function InviteForm({ org, plants, onDone }: { org: string; plants: MembersData['plants']; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [plantIds, setPlantIds] = useState<number[]>([]);
  const invite = useMutation({
    mutationFn: () => unwrap(api.POST('/organizations/{org}/invitations', { params: { path: { org } }, body: { email, role, plantIds: role === 'viewer' ? plantIds : [] } })),
    onSuccess: onDone,
  });
  if (invite.data) {
    return (
      <div className="space-y-3 text-sm">
        <p>Send this link to <span className="font-medium">{email}</span>. It works for 7 days.</p>
        <CopyField value={invite.data.url} label="Invitation link" />
        <p className="text-muted">They must sign in with exactly this address (Microsoft or Google). An account with another address can't accept it.</p>
      </div>
    );
  }
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); invite.mutate(); }}>
      <Field label="Email" hint="The address of their Microsoft or Google account">
        <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
      </Field>
      <RolePlants role={role} setRole={setRole} plantIds={plantIds} setPlantIds={setPlantIds} plants={plants} />
      <ErrorNote error={invite.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={invite.isPending || (role === 'viewer' && plantIds.length === 0 && plants.length > 0)}>
        Create invitation link
      </Button>
    </form>
  );
}

function AccessForm({ org, member, plants, onDone }: { org: string; member: Member; plants: MembersData['plants']; onDone: () => void }) {
  const [role, setRole] = useState<Role>(member.role);
  const [plantIds, setPlantIds] = useState<number[]>(member.plantIds);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT('/organizations/{org}/members/{userId}', { params: { path: { org, userId: member.userId } }, body: { role, plantIds: role === 'viewer' ? plantIds : [] } })),
    onSuccess: onDone,
  });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
      <RolePlants role={role} setRole={setRole} plantIds={plantIds} setPlantIds={setPlantIds} plants={plants} />
      <ErrorNote error={save.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={save.isPending}>Save</Button>
    </form>
  );
}
